import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { meApi } from "../api/me";
import { AccountMenu } from "./AccountMenu";
import { useAdminWorkspace } from "../features/admin/useAdminWorkspace";
import { AppLauncher, type LauncherProduct } from "./AppLauncher";
import { useMe } from "./hooks/useMe";

export type AppContext =
  | { kind: "personal" }
  | { kind: "product"; slug: string; name?: string; role: string }
  | { kind: "platform" };

/**
 * The one place that says where you are and lets you go elsewhere (docs/15 §1): the launcher
 * opens Personal, the products you may open and, for an owner, Platform. Being staff looks
 * different from being yourself, so the console wears the dark brand surface and shows your role.
 */
export function AppHeader({ context }: { context: AppContext }) {
  const me = useMe();
  const workspace = useAdminWorkspace();
  const onConsole = context.kind !== "personal";
  const current =
    context.kind === "product"
      ? (workspace.products.find((p) => p.slug === context.slug)?.name ??
        context.name ??
        context.slug)
      : context.kind === "platform"
        ? "Intelibo admin"
        : null;
  // The launcher shows what this person can open: the products they use and the ones they staff.
  const mine = useQuery({
    queryKey: ["me", "products"],
    queryFn: () => meApi.products(),
    enabled: !!me.data,
  });
  const tiles = new Map<string, LauncherProduct>();
  for (const product of mine.data ?? []) {
    tiles.set(product.slug, {
      slug: product.slug,
      name: product.name,
      logo_url: product.logo_url,
      href: product.web_url ?? undefined,
    });
  }
  for (const product of workspace.products) {
    tiles.set(product.slug, {
      slug: product.slug,
      name: product.name,
      logo_url: tiles.get(product.slug)?.logo_url ?? product.logo_url ?? null,
      to: `/admin/p/${product.slug}`,
    });
  }

  return (
    <header
      className={
        onConsole
          ? "bg-brand-dark text-white"
          : "border-b border-slate-200 bg-white"
      }
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-2">
        <Link to="/account" aria-label="Intelibo">
          {/* The on-dark wordmark knocks its eyes out in #1E1A55, which is exactly the console
              surface, so it only reads correctly on the dark header. */}
          <img
            src={
              onConsole ? "/intelibo-logo-on-dark.svg" : "/intelibo-logo.svg"
            }
            alt=""
            className="h-6 w-auto"
          />
        </Link>
        {current ? (
          <span className="flex items-center gap-2 text-sm">
            <span aria-hidden="true" className="opacity-50">
              /
            </span>
            <span className="font-medium">{current}</span>
            {context.kind === "product" ? (
              <span className="rounded-pill bg-white/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
                {context.role}
              </span>
            ) : null}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <AppLauncher
            products={[...tiles.values()]}
            showPlatform={workspace.platformRole === "owner"}
            onConsole={onConsole}
          />
          <AccountMenu me={me.data ?? undefined} onConsole={onConsole} />
        </div>
      </div>
    </header>
  );
}
